import { Download, ExternalLink, FileText, LoaderCircle, RotateCw } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import type { PdfDisposition } from './api';
import { PHONE_BOTTOM_SHEET } from './phone-sheet';
import type { Report } from './types';
import { useOwnDetails } from './use-own-details';
import { useNotePdf } from './use-report-pdf';

/** «Apri» and «Scarica»: in the bar from the tablet up, in the sheet on the phone (U7). */
function PdfButtons({
  busy,
  onRun,
  wide,
}: {
  readonly busy: PdfDisposition | null;
  readonly onRun: (disposition: PdfDisposition) => void;
  readonly wide: boolean;
}) {
  const { t } = useTranslation('expenses');
  const size = wide ? '' : 'min-h-11 w-full justify-start';
  const icon = (disposition: PdfDisposition, Idle: typeof Download) =>
    busy === disposition ? <LoaderCircle className="animate-spin" /> : <Idle />;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={size}
        disabled={busy !== null}
        aria-busy={busy === 'inline'}
        onClick={() => onRun('inline')}
      >
        {icon('inline', ExternalLink)}
        {t(wide ? 'reports.pdf.openWide' : 'reports.pdf.open')}
      </Button>
      <Button
        type="button"
        variant="outline"
        className={size}
        disabled={busy !== null}
        aria-busy={busy === 'attachment'}
        onClick={() => onRun('attachment')}
      >
        {icon('attachment', Download)}
        {t(wide ? 'reports.pdf.downloadWide' : 'reports.pdf.download')}
      </Button>
    </>
  );
}

/** The owner's own hint: the PDF prints placeholders until the profile has the details (R-c). */
function OwnDetailsHint({ owned }: { readonly owned: boolean }) {
  const { t } = useTranslation('expenses');
  const details = useOwnDetails(owned);
  if (details !== 'missing') return null;
  return (
    <span className="text-muted-foreground text-right text-sm" data-testid="pdf-own-details">
      {t('reports.pdf.ownDetails')}{' '}
      <Link to="/profile" className="text-foreground underline">
        {t('reports.pdf.profile')}
      </Link>
      {t('reports.pdf.profileEnd')}
    </span>
  );
}

/** The note's PDF, split so the bar puts its buttons in its own row and its lines under it. */
export type ReportPdfAction = {
  /** The buttons (and the phone's sheet), for the bar's row of actions. */
  readonly controls: ReactNode;
  /** «Preparo…», what went wrong, and the owner's hint, under the row (U9). */
  readonly messages: ReactNode;
};

/**
 * The note's PDF (R-d), when `can.downloadPdf` says the caller may have it; it is never worked out
 * here. Empty notes can be printed too.
 */
export function useReportPdfAction(report: Report): ReportPdfAction | null {
  const { t } = useTranslation('expenses');
  const pdf = useNotePdf();
  const busy = pdf.busy?.disposition ?? null;
  const [sheet, setSheet] = useState(false);
  if (report.can?.downloadPdf !== true) return null;
  const run = (disposition: PdfDisposition) => {
    setSheet(false);
    void pdf.run(report, disposition);
  };

  const controls = (
    <span
      role="group"
      aria-label={t('reports.pdf.group')}
      className="flex flex-wrap gap-2"
      data-testid="report-pdf"
    >
      <span className="hidden gap-2 @min-[620px]/shell:flex">
        <PdfButtons busy={busy} onRun={run} wide />
      </span>
      <Button
        type="button"
        variant="outline"
        className="min-h-11 @min-[620px]/shell:hidden"
        disabled={busy !== null}
        aria-busy={busy !== null}
        onClick={() => setSheet(true)}
      >
        {busy === null ? <FileText /> : <LoaderCircle className="animate-spin" />}
        {t('reports.pdf.phone')}
      </Button>
      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent side="bottom" className={`${PHONE_BOTTOM_SHEET} flex flex-col gap-3 p-4`}>
          <SheetTitle>{t('reports.pdf.sheetTitle', { number: report.number })}</SheetTitle>
          <SheetDescription>{t('reports.pdf.sheetHelp')}</SheetDescription>
          <PdfButtons busy={busy} onRun={run} wide={false} />
        </SheetContent>
      </Sheet>
    </span>
  );

  const messages = (
    <>
      {busy === null ? null : (
        <span role="status" className="text-muted-foreground text-sm">
          {t('reports.pdf.preparing')}
        </span>
      )}
      {pdf.error === null ? null : (
        <span
          role="alert"
          className="text-destructive flex flex-wrap items-center justify-end gap-2 text-right text-sm"
        >
          {t(`reports.pdf.errors.${pdf.error.code}`)}
          {pdf.error.code === 'NOTE_PDF_BUSY' ? (
            <Button
              type="button"
              variant="outline"
              className="min-h-11 @min-[620px]/shell:h-8 @min-[620px]/shell:min-h-0"
              onClick={pdf.retry}
            >
              <RotateCw />
              {t('reports.pdf.retry')}
            </Button>
          ) : null}
        </span>
      )}
      <OwnDetailsHint owned={report.can.owned === true} />
    </>
  );

  return { controls, messages };
}
